import type {PublicMethodUtils} from '@wix/document-services-types'

export default function (publicMethodUtils: PublicMethodUtils) {
    const {
        extensionPublicAPI: {immediate, getter}
    } = publicMethodUtils

    return {
        methods: {
            /** @class documentServices.contexts*/
            contexts: {
                /**
                 * Attaches a context to a component
                 * @member documentServices.contexts.attach
                 * @param {CompRef} compRef - the component to attach the context to
                 * @param {string} contextType - the context type to attach to the component
                 * @param {Object} [data] - optional initial data to attach to the context
                 */
                attach: immediate('contexts.attach'),
                /**
                 * Detaches a context from a component
                 * @member documentServices.contexts.detach
                 * @param {CompRef} compRef - the component to detach the context from
                 * @param {string} contextType - the context type to detach from the component
                 */
                detach: immediate('contexts.detach'),
                /**
                 * Returns all attached contextTypes of a bound object
                 * @member documentServices.contexts.list
                 * @param {BoundObjectRef} boundRef - the bound object whose attached contexts are requested
                 * @param {Object} [options] - optional options to include ancestors in the list
                 * @returns {Object[]} - an array of objects with 'contextType' and 'target' properties of all attached contexts
                 * 'target' can be 'self' (contexts attached on the bound object passed as boundRef) or 'above' (contexts attached on ancestors)
                 */
                list: getter('contexts.list'),
                /** @class documentServices.contexts.data*/
                data: {
                    /**
                     * Returns the context data for a specific context
                     * @member documentServices.contexts.data.get
                     * @param {CompRef} compRef - the component whose data is requested
                     * @param {string} contextType - the context type whose data is requested
                     * @returns {Object|undefined} - the context data, or undefined if no data exists
                     */
                    get: getter('contexts.data.get'),
                    /**
                     * Partially updates the context data for a specific context
                     * @member documentServices.contexts.data.update
                     * @param {CompRef} compRef - the component whose data should be updated
                     * @param {string} contextType - the context type whose data should be updated
                     * @param {Object} data - the new context data to merge with existing data
                     */
                    update: immediate('contexts.data.update'),
                    /**
                     * Removes all data items from a context
                     * @member documentServices.contexts.data.remove
                     * @param {CompRef} compRef - the component whose data should be removed
                     * @param {string} contextType - the context type whose data should be removed
                     */
                    remove: immediate('contexts.data.remove'),

                    /**
                     * Removes specific properties from a context's data
                     * @member documentServices.contexts.data.removeProperties
                     * @param {CompRef} compRef - the component whose properties should be removed
                     * @param {string} contextType - the context type whose properties should be removed
                     * @param {string[]} properties - the properties to remove
                     */
                    removeProperties: immediate('contexts.data.removeProperties')
                }
            }
        }
    }
}
